import React from 'react';
import { getActiveElementIn, getDialogTabbables } from './dialogFocus';

/**
 * Keeps Tab and Shift+Tab inside the dialog. MUI's TrapFocus enforcement
 * cannot do this here: it compares against document.activeElement, which is
 * the shadow host while focus is inside the widget.
 *
 * Listens on the document so a Tab pressed while focus is on the host page
 * (for example right after the launcher unmounted) also lands in the dialog.
 * A Tab from a focused host-page control is left alone. The country popup handles its own Tab and calls preventDefault, so it is skipped.
 */
export function useDialogFocusLoop(
  dialogRef: React.RefObject<HTMLElement | null>,
  active: boolean
) {
  React.useEffect(() => {
    if (!active) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Tab' || event.defaultPrevented) return;
      const dialog = dialogRef.current;
      if (!dialog) return;

      const tabbables = getDialogTabbables(dialog);
      if (tabbables.length === 0) {
        event.preventDefault();
        return;
      }
      const first = tabbables[0];
      const last = tabbables[tabbables.length - 1];
      const current = getActiveElementIn(dialog);
      const inside = current instanceof HTMLElement && dialog.contains(current);

      if (!inside) {
        // A focused host-page control keeps its native Tab: pull focus in only
        // from body or the widget host (e.g. right after the launcher unmounted).
        const host = dialog.getRootNode();
        const widgetHost = host instanceof ShadowRoot ? host.host : null;
        const outside = document.activeElement;
        if (outside && outside !== document.body && outside !== widgetHost) {
          return;
        }
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      } else if (event.shiftKey && (current === first || current === dialog)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && current === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [dialogRef, active]);
}
